import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from "react-redux";
import AsyncSelect from 'react-select/async';
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup";
import Select from "react-select";
import client from '../../../lib/feathers';
import Modal from '../../Modal';
import Page from '../../Page';
// import Breadcrumb from '../Breadcrumb';
import PrimaryButton from '../../PrimaryButton';
import getVintages from '../../../utilities/getVintages';
import {
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  MDXEditor,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import MergeLibationModal from './MergeLibationModal';
import MoveVintageModal from './MoveVintageModal';
import MergeVintageModal from './MergeVintageModal';
import VintageFormats from './VintageFormats';
import LibationAttachments from './LibationAttachments';
import toast from 'react-hot-toast';

const schema = yup.object({

  abv: yup.number()
  .nullable()
  .transform((_, val) => (val !== "" ? Number(val) : null))
  .optional()
  .min(0, "ABV cannot be less than 0")
  .max(100, "ABV cannot be more than 100")
});

const Tooltip = ({ children }) => (
  <div className="group/info">
    <span className="mr-1 flex w-4 h-4 bg-black rounded-full me-1.5 flex-shrink-0"><FontAwesomeIcon icon={faCircleQuestion} className="text-white" /></span>
    <span className="hidden absolute z-10 -top-6 rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20 group-hover/info:block">
      {children}
    </span>
  </div>
);

export default function LibationVintageForm() {

  const { lid, wid, vid } = useParams();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.user);

  const [currentLibationVintage, setCurrentLibationVintage] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showMergeVintageModal, setShowMergeVintageModal] = useState(false);
  const [isLoading, setIsLoading] = useState();

  const {
    handleSubmit,
    register,
    control,
    setValue,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const loadLibation = async () => {
    if (user) {
      setIsLoading(true);
      const service = client.service(`${user.role}/libation-vintage`);
      const response = await service.get(vid);
      setCurrentLibationVintage(response);
      
      setValue('tastingNotes', response.tastingNotes);
      setValue('abv', response.abv);
      setValue('msrp', response.msrp);
      setValue('production', response.production);
      setValue('soil', response.soil);
      setValue('elevation', response.elevation);
      setValue('harvestedAt', response.harvestedAt ? response.harvestedAt.split('T')[0] : '');
      setValue('bottledAt', response.bottledAt ? response.bottledAt.split('T')[0] : '');
      setValue('drinkByStart', response.drinkByStart || '');
      setValue('drinkByEnd', response.drinkByEnd || '');
      setValue('winemakeing', response.winemakeing);
      setValue('aging', response.aging);
      setValue('clones', response.clones);
      setValue('farming', response.farming);
      setValue('lifespan', response.lifespan);
      
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadLibation();
    // loadSelectors();
  }, [wid, user, vid]);

  const handleUpdate = async (data) => {
    console.log('handleing updates')
    try {
      const service = client.service(`${user.role}/libation-vintage`)

      // Transform empty strings to null for date fields
      const cleanedData = {
        ...data,
        harvestedAt: data.harvestedAt || null,
        bottledAt: data.bottledAt || null,
        drinkByStart: data.drinkByStart || null,
        drinkByEnd: data.drinkByEnd || null,
      };

      await service.patch(currentLibationVintage.id, cleanedData);

      navigate(`/${user.role}/listings/${lid}/wines`);
    } catch (error) {
      console.error("Error updating libation vintage: ", error);
    }
  }

  const handleDeleteLibation = async () => {
    const service = client.service(`${user.role}/libation-vintage`);
    await service.remove(vid);
    navigate(`/${user.role}/listings/${lid}/wines`);
  };

  const handleMoveVintage = async (targetLibation) => {
    try {
      const service = client.service(`${user.role}/libation-vintage`);
      await service.patch(currentLibationVintage.id, {
        libationId: targetLibation.id
      });
      
      toast.success("Vintage moved successfully!");
      setShowMoveModal(false);
      navigate(`/${user.role}/listings/${lid}/wines`);
    } catch (error) {
      console.error("Error moving vintage:", error);
      toast.error("Failed to move vintage. Please try again.");
    }
  };

  const handleMergeVintage = async (targetId) => {
    try {
      const service = client.service('admin/libation-vintage-merge');
      const result = await service.update(currentLibationVintage.id, { targetId });
      const moved = result.moved || {};
      const parts = [];
      if (moved.sips) parts.push(`${moved.sips} sips`);
      if (moved.cellarEntries) parts.push(`${moved.cellarEntries} library entries`);
      if (moved.flightEntries) parts.push(`${moved.flightEntries} flight entries`);
      if (moved.scans) parts.push(`${moved.scans} scans`);
      const summary = parts.length > 0 ? `Moved ${parts.join(', ')}` : 'Merge complete';
      toast.success(summary);
      setShowMergeVintageModal(false);
      navigate(`/${user.role}/listings/${lid}/wines`);
    } catch (error) {
      console.error("Error merging vintage:", error);
      toast.error(error.message || "Failed to merge vintage. Please try again.");
    }
  };

  const hasAssociatedData = (currentLibationVintage?.counts?.userSips > 0 || currentLibationVintage?.counts?.flights > 0);
  const contactMessage = <p>Please contact <a href="mailto:service@vinoseeker.com" className="underline">service@vinoseeker.com</a> to request any merges or deletions.</p>;

  const SaveToolBar = ({showMerge = true}) => (
    <div className="flex justify-between pt-5 relative">
      <div className="flex">
        <PrimaryButton
          className="disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
          onClick={() => setShowDeleteModal(true)}
        >
          Delete
        </PrimaryButton>
        {user.role === "admin" && (
          <>
            <button
              type="button"
              className="inline-flex mx-2 items-center px-4 py-2 border border-primary rounded-md shadow-sm text-sm font-medium text-primary bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              onClick={() => setShowMoveModal(true)}
            >
              Move to Different Libation
            </button>
            <button
              type="button"
              className="inline-flex mx-2 items-center px-4 py-2 border border-red-400 rounded-md shadow-sm text-sm font-medium text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={() => setShowMergeVintageModal(true)}
            >
              Merge into Another Vintage
            </button>
          </>
        )}
      </div>
      <div>
        <Link
          to={`/${user?.role}/listings/${lid}/wines`}
          type="button"
          className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Cancel
        </Link>
        <PrimaryButton type="submit">Save</PrimaryButton>
      </div>
    </div>
  );

  // Tailwind Styles //
  const errorFocus = 'border-red-400 focus:border-red-500 focus:ring-red-500';
  const nonErrorFocus = 'border-gray-300 focus:ring-gray-500 focus:border-gray-500';

  return (
    <Page>
      {!isLoading ? (
        <>
          <form onSubmit={(handleSubmit(handleUpdate))}>
            <SaveToolBar showMerge={false}/>

            {/* CARD */}
            <div className="bg-white shadow px-4 py-5 sm:rounded-lg my-5">
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 inline-block align-middle mr-2">
                  {currentLibationVintage?.vintage.year === 9999 ? 'NV' : currentLibationVintage?.vintage.year} - {currentLibationVintage?.libation.name}
                  </h3>
                  {!currentLibationVintage?.libation.needsReview
                    ? <span className="inline-flex items-center rounded-md bg-green-50 p-1 text-xs text-green-700 ring-1 ring-inset ring-green-600/20">Approved</span>
                    : <span className="inline-flex items-center rounded-md bg-yellow-50 p-1 text-xs text-yellow-700 ring-1 ring-inset ring-yellow-600/20">Pending</span>
                  }
                  <br />
                  <p className="mt-1 text-sm text-gray-500">{currentLibationVintage?.needsReview ? 'Review ' : 'Update '} the information for this vintage.</p>
                  <br />
                </div>

                <div className="mt-5 md:mt-0 md:col-span-2">
                  <div className="grid grid-cols-6 gap-6">
                    {/* <div className="col-span-6">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        {...register("name")}
                        className={`mt-1  block w-full shadow-sm sm:text-sm ${errors.name ? errorFocus : nonErrorFocus} rounded-md`}
                      />
                      {errors.name && <span className="text-red-400">{errors.name.message}</span>}
                    </div> */}


                    {/* <div className="col-span-6">
                      <div className="flex gap-1 items-center relative">
                        <label htmlFor="varietalId" className="block text-sm font-medium text-gray-700">
                          AVA
                        </label>
                        <Tooltip>
                          American Viticultural Area
                        </Tooltip>
                      </div>
                      <input
                        type="text"
                        name="ava"
                        placeholder="AVA"
                        {...register("ava")}
                        className={`block w-full shadow-sm sm:text-sm rounded-md ${nonErrorFocus}`}
                      />
                    </div> */}

                    {/* <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                      <label htmlFor="libationTypeId" className="block text-sm font-medium text-gray-700">
                        Type
                      </label>
                      {types && (
                        <Controller
                          name="libationTypeId"
                          control={control}
                          render={({ field: { onChange, value, ref } }) => (
                            <Select
                              ref={ref}
                              options={sortTypes(types)}
                              value={types.find((v) => v.id == value)}
                              onChange={(val) => onChange(val.value)}
                            />
                          )}
                        />
                      )}
                    </div> */}

                    {/* <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                      <label htmlFor="varietalId" className="block text-sm font-medium text-gray-700">
                        Varietal
                      </label>
                      {varietals && (
                        <Controller
                          name="varietalId"
                          control={control}
                          render={({ field: { onChange, value, ref } }) => (
                            <Select
                              ref={ref}
                              options={varietals}
                              value={varietals.find((v) => v.id == value)}
                              onChange={(val) => onChange(val.value)}
                            />
                          )}
                        />
                      )}
                    </div> */}

                    {/* <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                      <label htmlFor="vintage" className="block text-sm font-medium text-gray-700">
                        Vintage
                      </label>
                      {vintageList && (
                        <Controller
                          name="vintage"
                          control={control}
                          render={({ field: { onChange, value, ref } }) => (
                            <Select
                              ref={ref}
                              options={vintageList}
                              value={vintageList.find((v) => v.value === value)}
                              onChange={(val) => onChange(val.value)}
                              styles={{
                                control: (baseStyles) => ({
                                  ...baseStyles,
                                  borderColor: errors.varietalId ? 'red' : '#CCCCCC',
                                }),
                              }}
                            />
                          )}
                        />
                      )}
                      {errors.vintage && <span className="text-red-400">{errors.vintage.message}</span>}
                    </div> */}


                    {/* MDX toolbar z index changed to avoid overlap with search menu */}
                    <div className="col-span-6 z-0">
                      <label htmlFor="tastingNotes" className="block text-sm font-medium text-gray-700">
                        Tasting Notes
                      </label>
                      <Controller
                        name="tastingNotes"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <MDXEditor
                            ref={ref}
                            markdown={value || ''}
                            onChange={onChange}
                            placeholder={"Enter tasting notes of 1-2 sentences for this wine."}
                            className="border rounded-lg mt-1 h-52 overflow-y-auto [&_li]:ml-4 [&_ul]:list-disc [&_ol]:list-decimal"
                            plugins={
                              [
                                markdownShortcutPlugin(),
                                toolbarPlugin({
                                  toolbarContents: () => (
                                    <>
                                      {' '}
                                      <BoldItalicUnderlineToggles />
                                    </>
                                  )
                                })
                              ]}
                          />
                        )}
                      />
                    </div>

                    <div className="relative col-span-6 sm:col-span-3 lg:col-span-3">
                      <div className="flex gap-1 items-center relative">
                        <label htmlFor="varietalId" className="block text-sm font-medium text-gray-700">
                          ABV
                        </label>
                        <Tooltip>
                          Alcohol by Volume
                        </Tooltip>
                      </div>
                      <div className="relative">
                        <span className="absolute flex inset-y-4 left-2 items-center text-sm">%</span>
                        <input
                          name="abv"
                          type="number" 
                          step={0.1}
                          placeholder={'0.00'}
                          {...register("abv")}
                          className={`block w-full pl-7 shadow-sm sm:text-sm ${errors.abv ? errorFocus : nonErrorFocus} rounded-md`}
                        />
                      </div>
                      {errors.abv && <span className="text-red-400">{errors.abv.message}</span>}
                    </div>

                    <div className="relative col-span-6 sm:col-span-3 lg:col-span-3">
                      <div className="flex gap-1 items-center relative">
                        <label htmlFor="varietalId" className="block text-sm font-medium text-gray-700">
                          MSRP
                        </label>
                        <Tooltip>
                          Manufacturer&apos;s Suggested Retail Price
                        </Tooltip>
                      </div>
                      <div className="relative">
                        <span className="absolute flex inset-y-4 left-2 items-center text-sm">$</span>
                        <input
                          name="msrp"
                          type="number"
                          min={0.00}
                          step={0.01}
                          placeholder={'0.00'}
                          {...register("msrp")}
                          className={`block w-full pl-5 shadow-sm sm:text-sm rounded-md ${nonErrorFocus}`}
                        />
                      </div>
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="production" className="block text-sm font-medium text-gray-700">
                        Production (cases)
                      </label>
                      <input
                        name="production"
                        type="number"
                        min={0}
                        placeholder={'Number of cases produced'}
                        {...register("production")}
                        className={`block w-full shadow-sm sm:text-sm rounded-md ${nonErrorFocus}`}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="soil" className="block text-sm font-medium text-gray-700">
                        Soil Type
                      </label>
                      <input
                        name="soil"
                        type="text"
                        placeholder={'Soil composition'}
                        {...register("soil")}
                        className={`block w-full shadow-sm sm:text-sm rounded-md ${nonErrorFocus}`}
                      />
                    </div>

                    <div className="col-span-6">
                      <label htmlFor="elevation" className="block text-sm font-medium text-gray-700">
                        Elevation
                      </label>
                      <input
                        name="elevation"
                        type="text"
                        placeholder={'Vineyard elevation'}
                        {...register("elevation")}
                        className={`block w-full shadow-sm sm:text-sm rounded-md ${nonErrorFocus}`}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="harvestedAt" className="block text-sm font-medium text-gray-700">
                        Harvest Date
                      </label>
                      <input
                        name="harvestedAt"
                        type="date"
                        {...register("harvestedAt")}
                        className={`block w-full shadow-sm sm:text-sm rounded-md ${nonErrorFocus}`}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="bottledAt" className="block text-sm font-medium text-gray-700">
                        Bottled Date
                      </label>
                      <input
                        name="bottledAt"
                        type="date"
                        {...register("bottledAt")}
                        className={`block w-full shadow-sm sm:text-sm rounded-md ${nonErrorFocus}`}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="drinkByStart" className="block text-sm font-medium text-gray-700">
                        Drink Window Start
                      </label>
                      <input
                        name="drinkByStart"
                        type="date"
                        {...register("drinkByStart")}
                        className={`block w-full shadow-sm sm:text-sm rounded-md ${nonErrorFocus}`}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="drinkByEnd" className="block text-sm font-medium text-gray-700">
                        Drink Window End
                      </label>
                      <input
                        name="drinkByEnd"
                        type="date"
                        {...register("drinkByEnd")}
                        className={`block w-full shadow-sm sm:text-sm rounded-md ${nonErrorFocus}`}
                      />
                    </div>

                    {!watch('drinkByStart') && !watch('drinkByEnd') && currentLibationVintage?.libation?.varietal?.agingStart != null && currentLibationVintage?.vintage?.year && currentLibationVintage.vintage.year !== 9999 && (
                      <div className="col-span-6">
                        <p className="text-sm text-gray-400 italic">
                          Based on varietal: {currentLibationVintage.vintage.year + currentLibationVintage.libation.varietal.agingStart} – {currentLibationVintage.vintage.year + currentLibationVintage.libation.varietal.agingEnd}
                        </p>
                      </div>
                    )}

                    <div className="col-span-6 z-0">
                      <label htmlFor="winemakeing" className="block text-sm font-medium text-gray-700">
                        Winemaking Notes
                      </label>
                      <Controller
                        name="winemakeing"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <MDXEditor
                            ref={ref}
                            markdown={value || ''}
                            onChange={onChange}
                            placeholder={"Enter winemaking process and techniques used."}
                            className="border rounded-lg mt-1 h-32 overflow-y-auto [&_li]:ml-4 [&_ul]:list-disc [&_ol]:list-decimal"
                            plugins={
                              [
                                markdownShortcutPlugin(),
                                toolbarPlugin({
                                  toolbarContents: () => (
                                    <>
                                      {' '}
                                      <BoldItalicUnderlineToggles />
                                    </>
                                  )
                                })
                              ]}
                          />
                        )}
                      />
                    </div>

                    <div className="col-span-6 z-0">
                      <label htmlFor="aging" className="block text-sm font-medium text-gray-700">
                        Aging Process
                      </label>
                      <Controller
                        name="aging"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <MDXEditor
                            ref={ref}
                            markdown={value || ''}
                            onChange={onChange}
                            placeholder={"Enter aging process details (barrel type, duration, etc.)."}
                            className="border rounded-lg mt-1 h-32 overflow-y-auto [&_li]:ml-4 [&_ul]:list-disc [&_ol]:list-decimal"
                            plugins={
                              [
                                markdownShortcutPlugin(),
                                toolbarPlugin({
                                  toolbarContents: () => (
                                    <>
                                      {' '}
                                      <BoldItalicUnderlineToggles />
                                    </>
                                  )
                                })
                              ]}
                          />
                        )}
                      />
                    </div>

                    <div className="col-span-6 z-0">
                      <label htmlFor="clones" className="block text-sm font-medium text-gray-700">
                        Clones
                      </label>
                      <Controller
                        name="clones"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <MDXEditor
                            ref={ref}
                            markdown={value || ''}
                            onChange={onChange}
                            placeholder={"Enter clone information used for this vintage."}
                            className="border rounded-lg mt-1 h-32 overflow-y-auto [&_li]:ml-4 [&_ul]:list-disc [&_ol]:list-decimal"
                            plugins={
                              [
                                markdownShortcutPlugin(),
                                toolbarPlugin({
                                  toolbarContents: () => (
                                    <>
                                      {' '}
                                      <BoldItalicUnderlineToggles />
                                    </>
                                  )
                                })
                              ]}
                          />
                        )}
                      />
                    </div>

                    <div className="col-span-6 z-0">
                      <label htmlFor="farming" className="block text-sm font-medium text-gray-700">
                        Farming Practices
                      </label>
                      <Controller
                        name="farming"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <MDXEditor
                            ref={ref}
                            markdown={value || ''}
                            onChange={onChange}
                            placeholder={"Enter farming practices (organic, biodynamic, etc.)."}
                            className="border rounded-lg mt-1 h-32 overflow-y-auto [&_li]:ml-4 [&_ul]:list-disc [&_ol]:list-decimal"
                            plugins={
                              [
                                markdownShortcutPlugin(),
                                toolbarPlugin({
                                  toolbarContents: () => (
                                    <>
                                      {' '}
                                      <BoldItalicUnderlineToggles />
                                    </>
                                  )
                                })
                              ]}
                          />
                        )}
                      />
                    </div>

                    <div className="col-span-6 z-0">
                      <label htmlFor="lifespan" className="block text-sm font-medium text-gray-700">
                        Drinking Window
                      </label>
                      <Controller
                        name="lifespan"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <MDXEditor
                            ref={ref}
                            markdown={value || ''}
                            onChange={onChange}
                            placeholder={"Enter optimal drinking window and aging potential."}
                            className="border rounded-lg mt-1 h-32 overflow-y-auto [&_li]:ml-4 [&_ul]:list-disc [&_ol]:list-decimal"
                            plugins={
                              [
                                markdownShortcutPlugin(),
                                toolbarPlugin({
                                  toolbarContents: () => (
                                    <>
                                      {' '}
                                      <BoldItalicUnderlineToggles />
                                    </>
                                  )
                                })
                              ]}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>

          {currentLibationVintage && (
            <>
              <VintageFormats libationVintageId={currentLibationVintage.id} />
              <LibationAttachments
                targetType="libationVintage"
                targetId={currentLibationVintage.id}
              />
              {currentLibationVintage.libationId && (
                <LibationAttachments
                  targetType="libation"
                  targetId={currentLibationVintage.libationId}
                  readOnly
                  badgeLabel="all vintages"
                  title="Libation-wide media"
                  description="Generic labels and trade documents from this wine's parent libation. Edit on the libation page."
                />
              )}
            </>
          )}
        </>
      ) : (
        <p>Loading Wine...</p>
      )
      }

      {
        showDeleteModal
        && (
          <Modal>
            <div className="mt-3 text-center sm:mt-5">
              {!hasAssociatedData && (
                <>
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                  Confirm Deletion
                </h3>
                <div className="mt-2">
                  <p className="text-base text-gray-500">
                    Are you sure you would like to delete this vintage?
                  </p>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                    <button type="button" onClick={handleDeleteLibation} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-icon text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:bg-red-700 sm:col-start-2 sm:text-sm">Confirm</button>
                    <button type="button" onClick={() => setShowDeleteModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:col-start-1 sm:text-sm">Cancel</button>
                  </div>
                </div>
                </>
              )}
              {hasAssociatedData && (
                <>
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                    Delete Unavailable
                  </h3>
                  <div className="mt-2">
                    <p className="text-base text-gray-500">
                      This wine has associated data and cannot be deleted.
                      {user.role !== 'admin' ? contactMessage : ' It may be merged with another wine instead.'}
                    </p>
                    <div className="mt-5 sm:mt-6 ">
                      <button type="button" onClick={() => setShowDeleteModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:col-start-1 sm:text-sm">OK</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Modal>
        )
      }

      {showMergeModal
        && (
          <Modal>
            <MergeLibationModal
              onCancelClick={() => setShowMergeModal(false)}
              libation={currentLibationVintage}
            />
          </Modal>
        )
      }

      {showMoveModal
        && (
          <Modal>
            <MoveVintageModal
              onCancelClick={() => setShowMoveModal(false)}
              onMoveClick={handleMoveVintage}
              libationVintage={currentLibationVintage}
            />
          </Modal>
        )
      }

      {showMergeVintageModal
        && (
          <Modal>
            <MergeVintageModal
              onCancelClick={() => setShowMergeVintageModal(false)}
              onMergeClick={handleMergeVintage}
              libationVintage={currentLibationVintage}
            />
          </Modal>
        )
      }
    </Page >
  )
}
